/**
 * Protocol Extractor API Service
 *
 * Communicates with the Protocol Extractor Lambda for:
 * - Element extraction (evaluations, conditions, variables, actions, text)
 * - Protocol structure bulk loading
 * - Form content on-demand loading
 * - Reference resolution
 */

import axios, { AxiosInstance } from 'axios';
import { config } from '../config';
import { ProtocolStructure, FormContent } from '../types';

class ExtractorService {
  private client: AxiosInstance;
  private accessToken: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: config.extractorApiUrl,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      timeout: 30000,
    });

    this.client.interceptors.request.use(
      (reqConfig) => {
        if (this.accessToken) {
          reqConfig.headers['Authorization'] = `Bearer ${this.accessToken}`;
        }
        if (process.env.NODE_ENV === 'development') {
          console.log(`[Extractor API] ${reqConfig.method?.toUpperCase()} ${reqConfig.url}`);
        }
        return reqConfig;
      },
      (error) => Promise.reject(error)
    );
  }

  setAccessToken(token: string | null) {
    this.accessToken = token;
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  /** Parse Lambda response body (handles double-encoded JSON) */
  private parseBody(data: any): any {
    if (data?.body) {
      return typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
    }
    return data;
  }

  // ============================================
  // ELEMENT EXTRACTION (Scope-Aware)
  // ============================================

  async getEvaluation(
    trialId: string,
    sessionId: string,
    platform: string,
    evalId: string,
    formId?: string,
    executionId?: string
  ): Promise<any | null> {
    try {
      const params: Record<string, string> = { platform, session_id: sessionId };
      if (formId) params.form_id = formId;
      if (executionId) params.execution_id = executionId;
      const response = await this.client.get(`/protocol/${trialId}/evaluations/${evalId}`, { params });
      return this.parseBody(response.data);
    } catch {
      return null;
    }
  }

  async getCondition(
    trialId: string,
    sessionId: string,
    platform: string,
    conditionId: string,
    formId?: string,
    executionId?: string
  ): Promise<any | null> {
    try {
      const params: Record<string, string> = { platform, session_id: sessionId };
      if (formId) params.form_id = formId;
      if (executionId) params.execution_id = executionId;
      const response = await this.client.get(`/protocol/${trialId}/conditions/${conditionId}`, { params });
      return this.parseBody(response.data);
    } catch {
      return null;
    }
  }

  async getVariable(
    trialId: string,
    sessionId: string,
    platform: string,
    variableId: string,
    formId?: string,
    executionId?: string
  ): Promise<any | null> {
    try {
      const params: Record<string, string> = { platform, session_id: sessionId };
      if (formId) params.form_id = formId;
      if (executionId) params.execution_id = executionId;
      const response = await this.client.get(`/protocol/${trialId}/variables/${variableId}`, { params });
      return this.parseBody(response.data);
    } catch {
      return null;
    }
  }

  async getAction(
    trialId: string,
    sessionId: string,
    platform: string,
    actionId: string,
    executionId?: string
  ): Promise<any | null> {
    try {
      const params: Record<string, string> = { platform, session_id: sessionId };
      if (executionId) params.execution_id = executionId;
      const response = await this.client.get(`/protocol/${trialId}/actions/${actionId}`, { params });
      return this.parseBody(response.data);
    } catch {
      return null;
    }
  }

  async getText(
    trialId: string,
    sessionId: string,
    platform: string,
    textId: string,
    formId?: string
  ): Promise<any | null> {
    try {
      const params: Record<string, string> = { platform, session_id: sessionId };
      if (formId) params.form_id = formId;
      const response = await this.client.get(`/protocol/${trialId}/text/${textId}`, { params });
      return this.parseBody(response.data);
    } catch {
      return null;
    }
  }

  // ============================================
  // BULK LOADING (Protocol Structure API)
  // ============================================

  /**
   * Get complete protocol structure for workspace initialization
   *
   * Returns: settings, variables, code lists, events, conditions,
   * evaluations, actions, forms metadata
   */
  async getProtocolStructure(
    trialId: string,
    sessionId: string,
    platform: string,
    executionId?: string
  ): Promise<ProtocolStructure> {
    const params: Record<string, string> = { platform, session_id: sessionId };
    if (executionId) params.execution_id = executionId;
    const response = await this.client.get(`/protocol/${trialId}/structure`, { params });
    return this.parseBody(response.data);
  }

  /**
   * Get form content on-demand when user navigates to a form
   *
   * include: 'meta' | 'ui' | 'all'
   */
  async getFormContent(
    trialId: string,
    sessionId: string,
    platform: string,
    formId: string,
    include: 'meta' | 'ui' | 'all' = 'all',
    executionId?: string
  ): Promise<FormContent> {
    const params: Record<string, string> = { platform, session_id: sessionId, include };
    if (executionId) params.execution_id = executionId;
    const response = await this.client.get(`/protocol/${trialId}/forms/${formId}/content`, { params });
    return this.parseBody(response.data);
  }

  /**
   * Get all forms from a session protocol
   */
  async getForms(
    trialId: string,
    sessionId: string,
    platform: string
  ): Promise<any[]> {
    const response = await this.client.get(`/protocol/${trialId}/forms`, {
      params: { platform, session_id: sessionId },
    });
    const data = this.parseBody(response.data);
    return data?.forms || [];
  }

  /**
   * Get all roles from a session protocol
   */
  async getRoles(
    trialId: string,
    sessionId: string,
    platform: string
  ): Promise<any[]> {
    const response = await this.client.get(`/protocol/${trialId}/roles`, {
      params: { platform, session_id: sessionId },
    });
    const data = this.parseBody(response.data);
    return data?.roles || [];
  }

  // ============================================
  // REFERENCE RESOLUTION
  // ============================================

  /**
   * Load references for specific IDs extracted from XML
   *
   * Resolves evaluations, conditions, variables, and actions in parallel.
   */
  async loadReferencesForIds(
    trialId: string,
    sessionId: string,
    platform: string,
    referenceIds: { evaluations: string[]; conditions: string[]; variables: string[]; actions: string[] },
    formId?: string,
    executionId?: string
  ): Promise<{
    evaluations: Record<string, any>;
    conditions: Record<string, any>;
    variables: Record<string, any>;
    actions: Record<string, any>;
  }> {
    const evaluations: Record<string, any> = {};
    const conditions: Record<string, any> = {};
    const variables: Record<string, any> = {};
    const actions: Record<string, any> = {};

    const promises: Promise<void>[] = [];

    for (const evalId of referenceIds.evaluations) {
      promises.push(
        this.getEvaluation(trialId, sessionId, platform, evalId, formId, executionId)
          .then((result) => { if (result) evaluations[evalId] = result; })
      );
    }

    for (const condId of referenceIds.conditions) {
      promises.push(
        this.getCondition(trialId, sessionId, platform, condId, formId, executionId)
          .then((result) => { if (result) conditions[condId] = result; })
      );
    }

    for (const varId of referenceIds.variables) {
      promises.push(
        this.getVariable(trialId, sessionId, platform, varId, formId, executionId)
          .then((result) => { if (result) variables[varId] = result; })
      );
    }

    for (const actionId of referenceIds.actions) {
      promises.push(
        this.getAction(trialId, sessionId, platform, actionId, executionId)
          .then((result) => { if (result) actions[actionId] = result; })
      );
    }

    await Promise.all(promises);

    return { evaluations, conditions, variables, actions };
  }
}

// Export singleton instance
export const extractorService = new ExtractorService();
